/**
 * WorkbenchChat — 온톨로지 워크벤치 다단 tool_use 에이전트 채팅.
 *
 * @assistant-ui/react 의 ChatThread + useBatchRuntime 을 사용해 재구현.
 * 서버 stateless — 대화 히스토리는 localStorage(pk.workbenchChat.v2) 에 보관.
 *
 * UX 원칙:
 *  - 4 도구(search_blocks / search_packs / suggest_slice / save_slice_to_service)를 활용해
 *    사용자가 에이전트와 의논하며 슬라이스를 설계한다.
 *  - confirm 모드: mutation 도구 호출 시 [적용]/[건너뛰기] 버튼으로 사용자가 직접 결정.
 *  - auto 모드: mutation 도구 즉시 실행.
 *  - 서비스 dropdown: 미선택 시 채팅 비활성화.
 *
 * localStorage 호환:
 *  - v1 키(pk.workbenchChat.v1) 는 이전 자체 구현이 사용하던 스키마 — 새 라이브러리 형식과
 *    호환되지 않아 새 키 pk.workbenchChat.v2 를 사용한다.
 *  - 이전 대화는 v2 전환 후 보존되지 않는다 (callout 에서 안내).
 */
import { useCallback, useEffect, useMemo, useState } from "react"
import type { ChatMessage, ToolUIMap } from "@/shared/chat"
import { ChatThread, useBatchRuntime } from "@/shared/chat"
import type { AdminClient, AgentToolCall, ServiceSummary } from "../../api/admin-client.ts"
import { AdminApiError } from "../../api/admin-client.ts"
import type { SlicePreview } from "./WorkbenchSlicePreview.tsx"
import {
	createSaveSliceCard,
	createSearchBlocksCard,
	createSearchPacksCard,
	createSuggestSliceCard
} from "./WorkbenchToolCards.tsx"
import { workbenchErrorMessage } from "./workbench-helpers.ts"

/** localStorage 키 — v2 (라이브러리 형식). v1 과 호환 안 됨 (의도적). */
const STORAGE_KEY = "pk.workbenchChat.v2"

/** 보관 최대 메시지 수. */
const MAX_MESSAGES = 80

// ─── Props ────────────────────────────────────────────────────────────────────

export interface WorkbenchChatProps {
	readonly client: AdminClient
	/**
	 * 슬라이스를 저장할 대상 서비스 slug.
	 * 지정하면 서비스 dropdown 의 초기 선택값으로 사용되며, 미지정 시 컴포넌트 내부에서
	 * 직접 서비스 목록을 로드해 dropdown 을 통해 선택하도록 유도한다.
	 * 유효한 slug 가 선택될 때까지 채팅 입력이 비활성화된다.
	 */
	readonly targetServiceSlug?: string
	/** search_blocks 결과를 받아 그래프에 즉시 반영 (Optimistic). */
	readonly onSearchBlocksResult?: (blockIds: ReadonlyArray<string>) => void
	/** suggest_slice 결과를 받아 그래프에 즉시 반영 (Optimistic). */
	readonly onSuggestSliceResult?: (preview: SlicePreview) => void
}

// ─── AgentChatResponse → ChatMessage 변환 ────────────────────────────────────

/**
 * AgentChatResponse (text + toolCalls) 를 assistant-ui ChatMessage 형식으로 변환.
 *
 * 텍스트 part + 도구 호출 part 를 하나의 assistant 메시지 content 배열로 조합한다.
 * 도구 호출은 `{ type: "tool-call", ... }` part 형태로 표현된다.
 *
 * pending 플래그는 tool-call part 의 result 에 `{ pending: true }` 로 포함시켜
 * SaveSliceCard 가 감지하도록 한다.
 */
function agentResponseToChatMessage(text: string, toolCalls: ReadonlyArray<AgentToolCall>): ChatMessage {
	type Part = ChatMessage["content"][number]
	const parts: Part[] = []

	if (text.trim()) {
		parts.push({ type: "text", text } as Part)
	}

	for (const tc of toolCalls) {
		parts.push({
			type: "tool-call",
			toolCallId: `tc-${tc.name}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
			toolName: tc.name,
			args: tc.input,
			result: tc.pending
				? { ...(typeof tc.output === "object" && tc.output !== null ? tc.output : {}), pending: true }
				: tc.output,
			isError: !!tc.error
		} as Part)
	}

	// parts 가 비면 빈 텍스트라도 넣어 empty content 방지
	if (parts.length === 0) {
		parts.push({ type: "text", text: "" } as Part)
	}

	return { role: "assistant", content: parts }
}

/**
 * ChatMessage history 를 agentChat API 의 messages 형식으로 변환.
 *
 * assistant-ui 의 ThreadMessageLike content 에서 텍스트만 뽑아 문자열로 평탄화한다.
 * 도구 호출 part 는 대화 이력 전송 시 포함하지 않는다 (서버 stateless 방식).
 */
function historyToApiMessages(
	history: ReadonlyArray<ChatMessage>
): ReadonlyArray<{ role: "user" | "assistant"; content: string }> {
	const out: Array<{ role: "user" | "assistant"; content: string }> = []
	for (const msg of history) {
		if (msg.role !== "user" && msg.role !== "assistant") continue
		let text = ""
		if (typeof msg.content === "string") {
			text = msg.content
		} else if (Array.isArray(msg.content)) {
			for (const part of msg.content) {
				if (part && typeof part === "object" && "type" in part) {
					const p = part as Record<string, unknown>
					if (p.type === "text" && typeof p.text === "string") {
						text += p.text
					}
				}
			}
		}
		out.push({ role: msg.role, content: text })
	}
	return out
}

// ─── 컴포넌트 ──────────────────────────────────────────────────────────────────

/**
 * WorkbenchChat — 다단 tool_use 에이전트 채팅 사이드바.
 *
 * @assistant-ui/react ChatThread + useBatchRuntime 기반으로 재구현.
 * 기존 직접 구현(599줄) 대비 ~200줄로 축소.
 */
export function WorkbenchChat({
	client,
	targetServiceSlug: initialSlug,
	onSearchBlocksResult,
	onSuggestSliceResult
}: WorkbenchChatProps) {
	// ─── 서비스 dropdown 상태 ──────────────────────────────────────────────────
	const [services, setServices] = useState<ReadonlyArray<ServiceSummary>>([])
	const [servicesLoading, setServicesLoading] = useState(true)
	const [selectedSlug, setSelectedSlug] = useState<string>(initialSlug ?? "")
	const [confirmMode, setConfirmMode] = useState<"confirm" | "auto">("confirm")

	// 마운트 시 서비스 목록 로드
	useEffect(() => {
		let cancelled = false
		setServicesLoading(true)
		void client
			.listServices()
			.then((list) => {
				if (cancelled) return
				const active = list.filter((s) => s.status === "active")
				setServices(active)
				// prop 으로 초기값이 없고 서비스가 1개뿐이면 자동 선택
				if (!initialSlug && active.length === 1) {
					setSelectedSlug(active[0]?.serviceSlug ?? "")
				}
			})
			.catch(() => {
				if (cancelled) return
				// 목록 로드 실패 시 조용히 무시 (빈 dropdown 유지)
			})
			.finally(() => {
				if (!cancelled) setServicesLoading(false)
			})
		return () => {
			cancelled = true
		}
	}, [client, initialSlug])

	const activeSlug = selectedSlug.trim()
	const canChat = activeSlug.length > 0

	// ─── pending tool 처리 콜백 (SaveSliceCard 에서 위임) ─────────────────────
	// pending 도구는 현재 architecture 상 "적용" 시 auto 모드로 재전송이 필요하다.
	// assistant-ui 의 external store runtime 에는 "특정 메시지 수정" API 가 없으므로
	// applyPendingTool 은 resetThread 후 재전송 대신, auto 모드로 빈 user 메시지 추가로 처리한다.
	// 단, 이 방식은 UX 상 대화 흐름이 자연스럽지 않을 수 있어 추후 개선 여지가 있다.
	// 현재는 단순히 auto 모드 setter 를 노출해 사용자가 "자동 적용"으로 전환하도록 유도한다.
	// "건너뛰기" 는 별도 버튼/콜백 없이 새 메시지 전송 또는 채팅 초기화로 처리.

	const handleApplyPendingTool = useCallback((_toolCallId: string) => {
		// 이 도구 호출에 대해 auto 모드 전환 → 다음 전송 시 자동 적용
		setConfirmMode("auto")
	}, [])

	// ─── 도구 카드 toolUI 매핑 ────────────────────────────────────────────────
	const toolUI = useMemo((): ToolUIMap => {
		const callbacks = {
			onSearchBlocksResult,
			onSuggestSliceResult,
			onApplyPendingTool: handleApplyPendingTool
		}
		return {
			search_blocks: createSearchBlocksCard(callbacks),
			search_packs: createSearchPacksCard(callbacks),
			suggest_slice: createSuggestSliceCard(callbacks),
			save_slice_to_service: createSaveSliceCard(callbacks, confirmMode)
		}
	}, [onSearchBlocksResult, onSuggestSliceResult, handleApplyPendingTool, confirmMode])

	// ─── Batch Runtime ─────────────────────────────────────────────────────────
	const { runtime, resetThread, isRunning } = useBatchRuntime({
		storageKey: STORAGE_KEY,
		fetcher: useCallback(
			async (
				history: ReadonlyArray<ChatMessage>,
				signal: AbortSignal
			): Promise<ReadonlyArray<ChatMessage>> => {
				if (!activeSlug) return []
				const messages = historyToApiMessages(history)
				// MAX_MESSAGES 초과 시 앞에서 자름
				const trimmed = messages.slice(-MAX_MESSAGES)
				try {
					const res = await client.agentChat({
						serviceSlug: activeSlug,
						messages: trimmed,
						confirmMode
					})
					// signal 이 abort 됐으면 응답을 버림
					if (signal.aborted) return []
					return [agentResponseToChatMessage(res.text, res.toolCalls)]
				} catch (err) {
					if (signal.aborted) return []
					// AdminApiError 는 사용자 친화 메시지로 변환
					const msg =
						err instanceof AdminApiError
							? workbenchErrorMessage(err.code, err.message)
							: ((err as Error).message ?? "알 수 없는 오류가 발생했습니다.")
					const errorMsg: ChatMessage = {
						role: "assistant",
						content: [{ type: "text", text: `오류: ${msg}` }]
					}
					return [errorMsg]
				}
			},
			[activeSlug, confirmMode, client]
		)
	})

	// ─── emptyHint ────────────────────────────────────────────────────────────
	const emptyHint = canChat ? (
		<span>
			온톨로지 워크벤치 에이전트에게 도움을 요청해 보세요.
			<br />
			예: <em>"행위자(actor) 카테고리 블럭 뭐가 있어?"</em> ·{" "}
			<em>"커머스 이벤트 추적 슬라이스를 만들어줘"</em>
		</span>
	) : (
		<span>서비스를 선택해야 채팅이 시작됩니다</span>
	)

	return (
		<section className="workbench-chat" aria-label="에이전트 워크벤치 채팅">
			{/* 안내 callout */}
			<div className="workbench-chat__callout" role="note">
				에이전트와 의논하며 블럭/팩 카탈로그를 탐색하고 슬라이스를 만들 수 있습니다. 저장 전 에이전트 제안을
				직접 확인하려면 '확인 후 적용'을 선택하세요.
			</div>

			{/* 헤더: 서비스 dropdown + 모드 토글 + 초기화 */}
			<div className="workbench-chat__head">
				<div className="workbench-chat__title">
					<svg
						className="icon"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						aria-hidden
					>
						<title>에이전트 채팅</title>
						<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
					</svg>
					<span>워크벤치 에이전트</span>
				</div>
				<div className="workbench-chat__head-right">
					{/* 대상 서비스 dropdown */}
					<div className="workbench-chat__service-field" aria-label="대상 서비스 선택">
						<select
							className="workbench-chat__service-select input select"
							value={selectedSlug}
							onChange={(e) => setSelectedSlug(e.target.value)}
							disabled={servicesLoading}
							aria-label="대상 서비스"
							title={
								canChat
									? `선택된 서비스: ${selectedSlug}`
									: "서비스를 선택하면 슬라이스 적용 도구가 활성화됩니다"
							}
						>
							<option value="">{servicesLoading ? "로딩 중…" : "서비스 선택"}</option>
							{services.map((s) => (
								<option key={s.serviceSlug} value={s.serviceSlug}>
									{s.displayName} ({s.serviceSlug})
								</option>
							))}
						</select>
					</div>

					{/* 확인 모드 토글 */}
					<fieldset className="workbench-chat__mode-toggle" aria-label="적용 모드 선택">
						<button
							type="button"
							className={`workbench-chat__mode-btn${confirmMode === "confirm" ? " is-active" : ""}`}
							onClick={() => setConfirmMode("confirm")}
							title="도구 호출 결과를 적용 전 사용자가 확인"
						>
							확인 후 적용
						</button>
						<button
							type="button"
							className={`workbench-chat__mode-btn${confirmMode === "auto" ? " is-active" : ""}`}
							onClick={() => setConfirmMode("auto")}
							title="도구 호출 결과를 즉시 적용"
						>
							자동 적용
						</button>
					</fieldset>

					{/* 대화 초기화 */}
					<button
						type="button"
						className="workbench-chat__clear"
						onClick={resetThread}
						title="대화 기록 초기화"
					>
						초기화
					</button>
				</div>
			</div>

			{/* 채팅 스레드 (메시지 + 입력창) */}
			<ChatThread
				runtime={runtime}
				placeholder={canChat ? "메시지를 입력하세요…" : "대상 서비스를 먼저 선택하세요"}
				emptyHint={emptyHint}
				disabledComposer={!canChat}
				isRunning={isRunning}
				toolUI={toolUI}
				className="workbench-chat__thread"
			/>
		</section>
	)
}
